import React, {
    useRef,
    useEffect,
    useState,
    useCallback,
    useImperativeHandle,
    forwardRef,
    ForwardRefRenderFunction,
} from "react";
import * as Blockly from "blockly";
import { javascriptGenerator } from "blockly/javascript";

// Blocs de base + traductions
import "blockly/blocks";
import "blockly/javascript";
import "blockly/msg/fr"; // ou en

import { initAllCustomBlocks } from "./blocks";
import { generateToolboxXML } from "./funcToolbox";
import { Button, Space } from "antd";
import { CodeOutlined, DeleteOutlined } from "@ant-design/icons";
import "./css/toolbox.css";
import "./css/workspace-theme.css";
import { BlocklyConfig, ToolboxBlock } from "../types/config";
import { PhaserConfig } from "../../Phaser/types/config";
import tilmiTheme from "./tilmiTheme";
import { registerTilmiRenderer } from "./tilmiRenderer";

export interface BlocklyWorkspaceRef {
    getCode: () => string;
    getWorkspaceXml: () => string;
    getWorkspace: () => Blockly.WorkspaceSvg | null;
}

interface BlocklyWorkspaceProps {
    config: BlocklyConfig;
    configPhaser?: PhaserConfig;
    onCodeChange?: (code: string) => void;
    height?: number | string;

}

declare global {
    interface Window {
        highlightBlock?: (blockId: string) => void;
    }
}

const BlocklyWorkspace: ForwardRefRenderFunction<
    BlocklyWorkspaceRef,
    BlocklyWorkspaceProps
> = ({ config, configPhaser, onCodeChange, height = '100%' }, ref) => {
    const blocklyDiv = useRef<HTMLDivElement | null>(null);
    const [workspace, setWorkspace] = useState<Blockly.WorkspaceSvg | null>(null);
    /**
       * Applique les propriétés (movable, deletable, editable)
       * définies dans la configuration sur les blocs déjà présents
       * dans le workspace.
       */
    const applyBlockPropertiesFromConfig = useCallback(
        (ws: Blockly.WorkspaceSvg) => {
            const blockMap = new Map<string, ToolboxBlock>();
            config.toolboxCategories.forEach((cat) => {
                cat.blocks.forEach((blk) => blockMap.set(blk.type, blk));
            });

            ws.getAllBlocks(false).forEach((blk) => {
                const props = blockMap.get(blk.type);
                if (!props) return;
                const isAdmin = config.adminMode;
                blk.setMovable(isAdmin || props.movable);
                blk.setDeletable(isAdmin || props.deletable);
                blk.setEditable(isAdmin || props.editable);
            });
        },
        [config]
    );

    useEffect(() => {
        if (!blocklyDiv.current) return;
        registerTilmiRenderer();

        // 1) Initialiser blocs custom, toolbox...
        initAllCustomBlocks(configPhaser?.characters, configPhaser?.objects, configPhaser?.texts);

        (window as any).Blockly = Blockly;
        const toolboxXml = generateToolboxXML(config);

        // 2) Créer le workspace
        const newWorkspace = Blockly.inject(blocklyDiv.current, {
            toolbox: toolboxXml,
            readOnly: false,
            trashcan: true,
            theme: tilmiTheme,
            zoom: {
                controls: true,
                wheel: true,
                startScale: 1.12,
                maxScale: 2.5,
                minScale: 0.65,
                scaleSpeed: 1.08,
            },
            grid: {
                spacing: 28,
                length: 1,
                colour: "#ccd4ff",
                snap: true,
            },
            renderer: "tilmiRenderer",
            move: {
                drag: true,
                scrollbars: {
                    horizontal: true,
                    vertical: true,
                },
                wheel: true,
            },
        });

        // 3) Charger XML initial si présent
        if (config.workspace) {
            const parser = new DOMParser();
            const xmlDoc = parser.parseFromString(config.workspace, "text/xml");
            Blockly.Xml.domToWorkspace(xmlDoc.documentElement, newWorkspace);            // Appliquer les propriétés aux blocs initialement chargés
            applyBlockPropertiesFromConfig(newWorkspace);
        }

        // 4) Écouteur "change" => mise à jour du code
        const onChangeHandler = () => {
            if (!newWorkspace) return;
            const code = javascriptGenerator.workspaceToCode(newWorkspace);
            onCodeChange?.(code);
        };
        newWorkspace.addChangeListener(onChangeHandler);

        // 5) Écouteur "BLOCK_DELETE" => empêcher la suppression des non-supprimables
        const onBlockDelete = (event: any) => {
            if (event.type === Blockly.Events.BLOCK_DELETE) {
                let oldJsonBlocks = event.oldJson;
                // Selon Blockly, oldJson peut être un seul objet ou un tableau
                if (!Array.isArray(oldJsonBlocks)) {
                    oldJsonBlocks = [oldJsonBlocks];
                }

                // Collecte des IDs de blocs "deletable: false"
                const nonDeletableBlocks: string[] = [];

                // Récupère tous les blocs non-supprimables (y compris ceux en next, sur plusieurs niveaux)
                oldJsonBlocks.forEach((rootJson: any) => {
                    findNonDeletableBlocksRec(rootJson, nonDeletableBlocks);
                });

                if (nonDeletableBlocks.length > 0) {
                    // (1) Annuler la suppression, car BLOCK_DELETE a déjà opéré
                    newWorkspace.undo(false);

                    // (2) Détacher chaque bloc non-supprimable de son parent
                    nonDeletableBlocks.forEach((blockId) => {
                        const childBlock = newWorkspace.getBlockById(blockId);
                        if (!childBlock) return;
                        const parentBlock = childBlock.getParent();

                        if (parentBlock) {
                            // Débranche ce bloc du parent
                            childBlock.unplug(false);

                            // Optionnel : le déplacer un peu
                            childBlock.moveBy(40, 40);
                        }
                    });

                    // (3) Re-supprimer les blocs racines autorisés
                    //     (ils n'ont plus les non-deletable attachés, donc la cascade ne les emporte plus)
                    oldJsonBlocks.forEach((rootJson: any) => {
                        const rootId = rootJson.id;
                        const rootBlock = newWorkspace.getBlockById(rootId);
                        if (!rootBlock) return;
                        // S'il est autorisé à être supprimé (block.isDeletable()),
                        // on le supprime :
                        if (rootBlock.isDeletable()) {
                            rootBlock.dispose(false);
                        }
                    });
                }
            }
        };
        newWorkspace.addChangeListener(onBlockDelete);

        // 6) highlightBlock (optionnel)
        window.highlightBlock = (blockId: string) => {
            if (newWorkspace) {
                newWorkspace.highlightBlock(blockId);
            }
        };

        // 7) Cleanup au démontage / avant ré-injection
        setWorkspace(newWorkspace);
        return () => {
            newWorkspace.removeChangeListener(onChangeHandler);
            newWorkspace.removeChangeListener(onBlockDelete);
            newWorkspace.dispose();
        };
    }, [
        config,
        configPhaser,
        onCodeChange,
    ]);

    useEffect(() => {
        if (!workspace || !blocklyDiv.current) return;

        const ro = new ResizeObserver(() => {
            Blockly.svgResize(workspace);
        });
        ro.observe(blocklyDiv.current);

        const onWinResize = () => Blockly.svgResize(workspace);
        window.addEventListener('resize', onWinResize);

        return () => {
            ro.disconnect();
            window.removeEventListener('resize', onWinResize);
        };
    }, [workspace]);

    // ======================
    //     Méthodes/ref
    // ======================
    const getGeneratedCode = useCallback(() => {
        if (!workspace) return "";
        return javascriptGenerator.workspaceToCode(workspace);
    }, [workspace]);

    const workspaceToXml = useCallback(() => {
        if (!workspace) return "";
        const dom = Blockly.Xml.workspaceToDom(workspace);
        return new XMLSerializer().serializeToString(dom);
    }, [workspace]);

    useImperativeHandle(ref, () => ({
        getCode: () => getGeneratedCode(),
        getWorkspaceXml: () => workspaceToXml(),
        getWorkspace: () => workspace,
    }));

    // ======================
    //     UI buttons
    // ======================
    const handleShowCode = () => {
        console.log(getGeneratedCode());
    };

    const handleClearWorkspace = () => {
        workspace?.clear();
    };

    // ======================
    //     Rendu JSX
    // ======================
    return (
        <div className="tilmi-blockly-container" style={{ display: 'flex', flexDirection: 'column' }}>
            <div
                ref={blocklyDiv}
                className="tilmi-blockly-workspace"
                style={{
                    height,                // <- utilise la prop
                    minHeight: 0,          // IMPORTANT pour flex parents
                    overflow: 'hidden',    // évite les scrollbars parasites
                }}
            />
            {config.adminMode && (
                <Space className="tilmi-toolbar">
                    <Button icon={<CodeOutlined />} onClick={handleShowCode}>Afficher le code</Button>
                    <Button danger icon={<DeleteOutlined />} onClick={handleClearWorkspace}>Vider le workspace</Button>
                </Space>
            )}
        </div>
    );
};

export default forwardRef(BlocklyWorkspace);

/**
 * Parcourt récursivement un bloc JSON pour trouver ceux ayant deletable=false,
 * quel que soit l'endroit (children + chainage next).
 */
function findNonDeletableBlocksRec(json: any, results: string[]) {
    if (!json || typeof json !== "object") return;

    // 1) Si ce bloc lui-même est non-supprimable
    if (json.deletable === false) {
        results.push(json.id);
    }

    // 2) Parcourir "children" (inputs imbriqués)
    if (Array.isArray(json.children)) {
        json.children.forEach((child: any) => {
            findNonDeletableBlocksRec(child, results);
        });
    }

    // 3) Parcourir la chaîne "next" (plusieurs blocs successifs)
    //    On peut faire une boucle while pour suivre toute la liste chaînée
    let nextLink = json.next;
    while (nextLink && nextLink.block) {
        findNonDeletableBlocksRec(nextLink.block, results);
        // Passer au "next" du bloc suivant, etc.
        nextLink = nextLink.block.next;
    }
}
